import { useEffect, useMemo, useState } from "react";
import { Button, Dialog, FormField, Select, useToast } from "@chotca/ui";
import { EmployerAnalyticsEvent } from "@chotca/config";
import { useCreateInvite, useOrgAddresses } from "@chotca/api-client";
import { ChotcaApiError } from "@chotca/sdk";
import { logAnalyticsEvent } from "@/lib/analytics";
import { SlotCalendar } from "./slot-calendar";

const MAX_PICK = 5;

/**
 * Employer-facing modal: "Mời ứng viên phỏng vấn".
 *
 * UX model:
 *   - One weekly calendar (Mon–Sun) per location (see {@link SlotCalendar}).
 *   - Tap a slot to include it as an option for this invite (max 5).
 *   - Submit sends N proposed time options; worker picks 1.
 */
export function InviteApplicantDialog({
  open,
  onClose,
  onInvited,
  orgId,
  applicationId,
  workerName,
}: {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful invite so callers can refresh applicant lists. */
  onInvited?: () => void;
  orgId: string;
  applicationId: string;
  workerName: string;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const addressesQ = useOrgAddresses(orgId);
  const addresses = useMemo(
    () => (addressesQ.data?.items ?? []).filter((a) => !a.archivedAt),
    [addressesQ.data],
  );

  const [locationId, setLocationId] = useState<string>("");
  const effectiveLocationId = locationId || addresses[0]?.id || "";
  const selectedAddress = useMemo(
    () => addresses.find((a) => a.id === effectiveLocationId) ?? null,
    [addresses, effectiveLocationId],
  );

  const [picked, setPicked] = useState<Set<string>>(new Set());

  // Reset state when modal closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setPicked(new Set());
      setLocationId("");
    }
  }, [open]);

  // Slots are scoped per location — switching location must clear picks.
  useEffect(() => {
    setPicked(new Set());
  }, [effectiveLocationId]);

  const createInvite = useCreateInvite(orgId);

  function togglePick(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_PICK) next.add(id);
      return next;
    });
  }

  async function onSubmit() {
    if (picked.size === 0 || !effectiveLocationId || createInvite.isPending)
      return;
    try {
      await createInvite.mutateAsync({
        applicationId,
        orgAddressId: effectiveLocationId,
        slotIds: Array.from(picked),
      });
      logAnalyticsEvent(EmployerAnalyticsEvent.applicantInvited, {
        slot_count: picked.size,
        source: "invite_dialog",
      });
      toastSuccess(`Đã gửi lời mời tới ${workerName}`);
      onInvited?.();
      onClose();
    } catch (e) {
      // Stale list still showed "Mời PV" for an already-invited applicant: the
      // invite exists, so refresh the list and close rather than alarm the user.
      if (e instanceof ChotcaApiError && e.code === "invite_already_exists") {
        onInvited?.();
        onClose();
        return;
      }
      toastError(e instanceof Error ? e.message : "Mời PV thất bại");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Mời ${workerName} phỏng vấn`}
      description="Đề xuất các khung giờ — ứng viên sẽ chọn 1 để xác nhận."
      size="lg"
      footer={
        <>
          <div className="mr-auto text-sm text-ash">
            {picked.size === 0
              ? "Chưa chọn khung giờ nào"
              : `Đã chọn ${picked.size}/${MAX_PICK} khung giờ`}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              picked.size === 0 ||
              !effectiveLocationId ||
              createInvite.isPending
            }
          >
            Gửi {picked.size > 0 ? `${picked.size} lựa chọn` : "lời mời"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {addresses.length === 0 && !addressesQ.isLoading ? (
          <div className="rounded-lg border border-smoke bg-mist/40 px-3 py-2 text-sm text-ash">
            Doanh nghiệp chưa có chi nhánh — thêm chi nhánh ở phần Cài đặt trước
            khi mời phỏng vấn.
          </div>
        ) : addresses.length > 1 ? (
          <FormField label="Chi nhánh phỏng vấn">
            <Select
              value={effectiveLocationId}
              onValueChange={setLocationId}
              options={addresses.map((a) => ({
                value: a.id,
                label: `${a.alias} — ${a.district}, ${a.city}`,
              }))}
            />
            {selectedAddress ? (
              <p className="mt-1 text-xs text-ash">{selectedAddress.address}</p>
            ) : null}
          </FormField>
        ) : selectedAddress ? (
          <div className="rounded-lg border border-smoke bg-mist/40 px-3 py-2">
            <p className="text-xs font-semibold text-ash">
              Chi nhánh phỏng vấn
            </p>
            <p className="text-sm font-semibold text-ink">
              {selectedAddress.alias}
            </p>
            <p className="text-xs text-ash">
              {selectedAddress.address} — {selectedAddress.district},{" "}
              {selectedAddress.city}
            </p>
          </div>
        ) : null}

        {effectiveLocationId ? (
          <SlotCalendar
            orgId={orgId}
            locationId={effectiveLocationId}
            mode="pick"
            picked={picked}
            onTogglePick={togglePick}
            maxPick={MAX_PICK}
            onCreated={(slot) =>
              setPicked((prev) => {
                const next = new Set(prev);
                if (next.size < MAX_PICK) next.add(slot.id);
                return next;
              })
            }
          />
        ) : null}
      </div>
    </Dialog>
  );
}
